import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { api, BACKEND_URL, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth";
import Logo from "../components/Logo";
import { ArrowRight, XCircle } from "@phosphor-icons/react";

export default function MemberShareLink() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [state, setState] = useState("loading"); // loading | preview | error
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [entering, setEntering] = useState(false);
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    (async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/m/${token}`);
        setInfo(data);
        setState("preview");
      } catch (e) {
        setError(formatApiErrorDetail(e.response?.data?.detail) || "Invalid or revoked share link.");
        setState("error");
      }
    })();
  }, [token]);

  const enter = async () => {
    setEntering(true);
    try {
      const { data } = await api.post(`/m/${token}/session`);
      await checkAuth();
      navigate(`/member/events/${data.event_id}`, { replace: true });
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Failed to sign in.");
      setState("error");
    } finally {
      setEntering(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center p-6">
      <div className="w-full max-w-md text-center">
        <Link to="/" className="inline-flex mb-8" data-testid="share-link-brand">
          <Logo size={44} />
        </Link>
        {state === "loading" && (
          <div data-testid="share-link-loading" className="mono-font text-sm text-white/60 tracking-widest uppercase pulse-glow">
            Verifying share link…
          </div>
        )}
        {state === "preview" && info && (
          <div className="border border-[#00FF66]/40 border-glow-green p-8 text-left" data-testid="share-link-preview">
            <div className="mono-font text-[10px] tracking-[0.3em] uppercase text-white/40">Membership · Share Link</div>
            <h1 className="heading-font text-2xl font-black tracking-tighter mt-3">
              Hello, {info.member_name}.
            </h1>
            <p className="text-white/70 text-sm mt-3">
              You've been given control of your distributor slot for:
            </p>
            <div className="mt-3 border border-white/10 p-3">
              <div className="mono-font text-[10px] tracking-[0.3em] uppercase text-white/40">Event</div>
              <div className="heading-font text-lg font-bold mt-1">{info.event_title}</div>
              <div className="mono-font text-xs text-[#00FF66] mt-2">{info.available} tickets available</div>
            </div>
            <button onClick={enter} disabled={entering} data-testid="share-link-enter"
              className="mt-6 w-full inline-flex items-center justify-center gap-3 px-5 py-3 bg-[#00FF66] text-black mono-font text-xs uppercase tracking-[0.25em] hover:bg-white transition-colors duration-200 disabled:opacity-60">
              {entering ? "Signing in…" : "Enter Portal"} <ArrowRight weight="bold" size={14} />
            </button>
            <div className="mono-font text-[10px] text-white/40 mt-4">
              This link only signs you in for this membership. The event creator can revoke it any time.
            </div>
          </div>
        )}
        {state === "error" && (
          <div className="border border-[#FF0033]/40 p-8" data-testid="share-link-error">
            <XCircle size={40} weight="fill" className="text-[#FF0033] mx-auto" />
            <h1 className="heading-font text-2xl font-black tracking-tighter mt-4">Link unavailable</h1>
            <p className="text-white/60 text-sm mt-2">{error}</p>
            <Link to="/login" className="mt-6 inline-flex items-center gap-3 px-5 py-3 border border-white/20 mono-font text-xs uppercase tracking-[0.25em] hover:border-white/60 transition-colors duration-200">
              Log in with password
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
